export type HealthInterestCategoryRef = {
  id: string;
  labelKey?: string;
  fallbackLabel?: string;
};

const toTrimmedString = (value: unknown): string => {
  if (value == null) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value).trim();
  }
  return "";
};

const normalizeMatchKey = (value: string): string =>
  value.trim().toUpperCase().replaceAll(/\s+/g, "_");

const pickRowString = (row: Record<string, unknown>, keys: string[]): string => {
  for (const key of keys) {
    const value = row[key];
    const trimmed = toTrimmedString(value);
    if (trimmed !== "") return trimmed;
  }
  return "";
};

/** Extract `userPersonalInterests` from login / profile payloads. */
export const extractUserPersonalInterestsArray = (
  payload: unknown
): unknown[] => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;

  const visited = new Set<unknown>();
  const walk = (node: unknown, depth = 0): unknown[] => {
    if (!node || depth > 5 || visited.has(node)) return [];
    visited.add(node);

    if (Array.isArray(node)) return node;

    if (typeof node !== "object") return [];

    const root = node as Record<string, unknown>;
    const keys = [
      "userPersonalInterests",
      "user_personal_interests",
      "personalInterests",
      "healthInterests",
      "userHealthInterests",
    ];

    for (const key of keys) {
      const value = root[key];
      if (Array.isArray(value) && value.length > 0) return value;
    }

    const nestedKeys = ["data", "patientProfile", "user", "profile"];
    for (const key of nestedKeys) {
      const found = walk(root[key], depth + 1);
      if (found.length > 0) return found;
    }

    return [];
  };

  return walk(payload);
};

/** Map login `userPersonalInterests` entries to category ids from GET /categories. */
export const mapPersonalInterestsToCategoryIds = (
  personalInterests: unknown[],
  categories: HealthInterestCategoryRef[]
): string[] => {
  if (!Array.isArray(personalInterests) || personalInterests.length === 0) {
    return [];
  }
  if (!categories.length) {
    return parseUserHealthInterestIds({ data: personalInterests });
  }

  const byId = new Map(categories.map((c) => [c.id, c.id]));
  const byGoal = new Map<string, string>();
  const byLabel = new Map<string, string>();

  categories.forEach((cat) => {
    if (cat.labelKey) {
      byGoal.set(normalizeMatchKey(cat.labelKey), cat.id);
    }
    if (cat.fallbackLabel) {
      byLabel.set(normalizeMatchKey(cat.fallbackLabel), cat.id);
    }
  });

  const selected = new Set<string>();

  personalInterests.forEach((item) => {
    if (item == null) return;

    if (typeof item === "string" || typeof item === "number") {
      const id = toTrimmedString(item);
      if (byId.has(id)) selected.add(id);
      return;
    }

    if (typeof item !== "object") return;

    const row = item as Record<string, unknown>;
    const idCandidate =
      row.healthInterestCategoryId ??
      row.categoryId ??
      row.healthInterestId ??
      row.interestId ??
      row.id;

    if (idCandidate != null) {
      const id = toTrimmedString(idCandidate);
      if (byId.has(id)) {
        selected.add(id);
        return;
      }
    }

    const goalType = pickRowString(row, [
      "goalType",
      "goal_type",
      "type",
      "code",
      "labelKey",
    ]);
    if (goalType) {
      const mapped = byGoal.get(normalizeMatchKey(goalType));
      if (mapped) {
        selected.add(mapped);
        return;
      }
    }

    const title = pickRowString(row, [
      "goalTitle",
      "goal_title",
      "name",
      "title",
      "label",
      "categoryName",
      "displayName",
    ]);
    if (title) {
      const mapped = byLabel.get(normalizeMatchKey(title));
      if (mapped) selected.add(mapped);
    }
  });

  return Array.from(selected);
};

/** Parse GET/POST user-interests response into string ids for the modal. */
export const parseUserHealthInterestIds = (response: unknown): string[] => {
  if (!response) return [];

  const collect = (value: unknown): string[] => {
    if (!Array.isArray(value)) return [];
    return value
      .map((id) => {
        if (id != null && typeof id === "object") {
          const row = id as Record<string, unknown>;
          const nested =
            row.id ?? row.healthInterestId ?? row.categoryId ?? row.interestId;
          return nested == null ? "" : toTrimmedString(nested);
        }
        return toTrimmedString(id);
      })
      .filter((id) => id !== "" && id !== "undefined" && id !== "null");
  };

  if (Array.isArray(response)) return collect(response);

  const root = response as Record<string, unknown>;
  const direct = collect(
    root.healthInterestIds ??
      root.interestIds ??
      root.userPersonalInterests ??
      root.personalInterests
  );
  if (direct.length > 0) return direct;

  const data = root.data;
  if (Array.isArray(data)) return collect(data);
  if (data && typeof data === "object") {
    const nested = data as Record<string, unknown>;
    const fromNested = collect(
      nested.healthInterestIds ??
        nested.interestIds ??
        nested.ids ??
        nested.userPersonalInterests ??
        nested.personalInterests
    );
    if (fromNested.length > 0) return fromNested;
  }

  return [];
};

export const toHealthInterestIdPayload = (selectedIds: string[]): number[] =>
  selectedIds.map(Number).filter((id) => Number.isFinite(id));
